import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { motion, useReducedMotion, useScroll } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, ArrowRight, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetClose } from "@/components/ui/sheet";
import { fetchSite } from "@/lib/api";
import { cn } from "@/lib/utils";
import { setHeaderHidden, useHeaderState } from "@/lib/headerTheme";
import { LogoMark } from "@/components/shared/Logo";
import { ChatHeaderButton, ChatLauncher } from "@/components/assistant/Chat";
import { MadeBy } from "@/components/shared/MadeBy";
import { NewsletterSignup } from "@/components/shared/NewsletterSignup";
import { usePageTracking } from "@/lib/track";

/** `match`: other paths that light up the same menu item. */
const NAV = [
  { to: "/arrangement", label: "Program", id: "events", match: ["/billett"] },
  { to: "/nytt", label: "Nytt", id: "news" },
  { to: "/rom", label: "Rom og prisar", id: "rooms", match: ["/prisar", "/leig-rom"] },
  { to: "/kalender", label: "Kalender", id: "calendar" },
  { to: "/om-oss", label: "Om huset", id: "about", match: ["/velforeininga"] },
  { to: "/historie", label: "Historie", id: "history" },
  { to: "/omradet", label: "Området", id: "area" },
  { to: "/kontakt", label: "Kontakt", id: "contact", match: ["/spor-oss"] },
];
const href = (n) => `${n.to}${n.hash || ""}`;

/** Same horizontal rhythm as <Container/> so the nav lines up with page content. */
const GUTTER = "mx-auto w-full max-w-site px-5 sm:px-8 lg:px-10";

/**
 * Official logotype (serif wordmark + «Nº 13» rule). `light` for dark surfaces, `compact` once the bar has condensed.
 */
export const Logo = ({ className, light = false, compact = false, testId = "topnav-logo-link" }) => (
  <Link
    to="/"
    className={cn("group inline-flex shrink-0 items-center transition-[opacity,width] duration-300 hover:opacity-80", light ? "text-paper" : "text-foreground", className)}
    data-testid={testId}
    aria-label="Halsnøy Kulturhus – til framsida"
  >
    <LogoMark className={cn("transition-[width] duration-300", compact ? "w-[188px] sm:w-[204px]" : "w-[188px] sm:w-[232px] lg:w-[252px]")} />
  </Link>
);

const MobileMenu = ({ site }) => {
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();
  const email = site?.contact_email || "post@halsnoykulturhus.no";
  const linkCls = ({ isActive }) => cn("flex items-center justify-between border-b border-paper/15 py-3.5 font-display text-[28px] font-semibold leading-none tracking-[-0.03em] text-paper/70 transition-colors hover:text-paper", isActive && "text-paper");
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button type="button" className="-mr-2 flex h-11 w-11 items-center justify-center rounded-full text-foreground transition-colors hover:bg-secondary lg:hidden" aria-label="Opne meny" data-testid="mobile-menu-open-button">
          <span className="flex flex-col gap-[6px]" aria-hidden="true"><span className="block h-[2px] w-6 rounded-full bg-current" /><span className="block h-[2px] w-6 rounded-full bg-current" /></span>
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="grain w-full border-0 bg-ink p-0 text-paper sm:max-w-md [&>button]:hidden" data-testid="mobile-menu">
        <div className="flex h-full flex-col overflow-y-auto overscroll-contain">
          <div className="sticky top-0 z-10 flex h-[68px] shrink-0 items-center justify-between border-b border-paper/15 bg-ink/95 px-5 backdrop-blur">
            <SheetTitle asChild>
              <span className="inline-flex items-center text-paper"><LogoMark className="w-[188px]" /></span>
            </SheetTitle>
            <SheetClose asChild>
              <button type="button" className="inline-flex h-10 items-center gap-2 rounded-full border border-paper/30 px-4 text-[13px] font-medium text-paper transition-colors hover:bg-paper hover:text-ink" aria-label="Lukk meny" data-testid="mobile-menu-close-button">Lukk <X className="h-3.5 w-3.5" /></button>
            </SheetClose>
          </div>
          <nav className="flex flex-col px-5 pt-2" aria-label="Mobilmeny">
            <NavLink to="/" end onClick={() => setOpen(false)} data-testid="mobile-nav-home-link" className={linkCls}>
              {({ isActive }) => (<><span>Heim</span>{isActive && <span className="h-2 w-2 rounded-full bg-sol" />}</>)}
            </NavLink>
            {NAV.map((n) => {
              const on = activeId(pathname, hash) === n.id;
              return (
                <Link key={n.id} to={href(n)} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.id}-link`} className={linkCls({ isActive: on })} aria-current={on ? "page" : undefined}>
                  <span>{n.label}</span>{on && <span className="h-2 w-2 rounded-full bg-sol" />}
                </Link>
              );
            })}
            <div className="mt-6 grid gap-2">
              <Button asChild variant="light" size="xl" className="justify-between" data-testid="mobile-nav-booking-link">
                <Link to="/leig-rom" onClick={() => setOpen(false)}>Leig rom <ArrowUpRight className="h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="lightSolOutline" size="xl" className="justify-between" data-testid="mobile-nav-arranger-link">
                <Link to="/arranger" onClick={() => setOpen(false)}>Arranger noko <Sparkles className="h-4 w-4" /></Link>
              </Button>
            </div>
          </nav>
          <div className="mt-8 border-t border-paper/15 px-5 pb-8 pt-6">
            <p className="t-mono text-paper/55">Kontakt</p>
            <a href={`mailto:${email}`} className="mt-2 block text-paper u-hover">{email}</a>
            <div className="mt-4 flex gap-5 text-[14px] font-medium">
              {site?.facebook_url && <a href={site.facebook_url} target="_blank" rel="noopener noreferrer" className="text-paper/80 u-hover hover:text-paper">Facebook</a>}
              {site?.instagram_url && <a href={site.instagram_url} target="_blank" rel="noopener noreferrer" className="text-paper/80 u-hover hover:text-paper">Instagram</a>}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

/**
 * Top bar – one flat, full-width line. At rest it sits transparent on the paper; once you scroll it condenses,
 * turns to frosted paper with a single hairline beneath and a thin line of light along the top that fills as you
 * read. It tucks away while you scroll down and comes back the moment you scroll up. Menu items carry a gold
 * hairline that slides between them (hover and the current page). No pills, no rounded box.
 */
const isMatch = (pathname, n) => [n.to, ...(n.match || [])].some((p) => pathname === p || pathname.startsWith(`${p}/`));
/** The one item that is «on»: a hash item wins while its section is the target, otherwise the plain path match. */
const activeId = (pathname, hash) => (NAV.find((n) => n.hash && pathname === n.to && hash === n.hash) || NAV.find((n) => !n.hash && isMatch(pathname, n)))?.id || null;

const TopNav = ({ site }) => {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [hover, setHover] = useState(null);
  const { pathname, hash } = useLocation();
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const { dark } = useHeaderState();
  useEffect(() => { setHeaderHidden(hidden); }, [hidden]);
  useEffect(() => {
    let last = window.scrollY;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      setScrolled(y > 24);
      if (y < 120 || y < last - 6) setHidden(false);
      else if (y > last + 6) setHidden(true);
      last = y;
      ticking = false;
    };
    const on = () => { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } };
    update();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const active = activeId(pathname, hash);
  const target = hover ?? active;

  return (
    <header
      className={cn(
        "sticky top-0 z-40 border-b transition-[transform,background-color,border-color,color] duration-300 ease-out",
        dark && "text-paper",
        scrolled
          ? dark ? "border-paper/10 bg-ink/70 backdrop-blur-xl" : "border-border/80 bg-background/85 backdrop-blur-xl supports-[backdrop-filter]:bg-background/75"
          : "border-transparent bg-transparent",
        hidden && "-translate-y-full"
      )}
      data-testid="site-header"
      data-scrolled={scrolled ? "true" : "false"}
      data-theme={dark ? "dark" : "light"}
    >
      {/* reading progress – a line of light along the top edge, only once you have scrolled */}
      <motion.span aria-hidden="true" className={cn("pointer-events-none absolute inset-x-0 top-0 h-[2px] origin-left bg-sol transition-opacity duration-300", scrolled ? "opacity-100" : "opacity-0")} style={{ scaleX: reduce ? 1 : scrollYProgress }} data-testid="site-header-progress" />
      <div className={cn(GUTTER, "flex items-center justify-between gap-3 transition-[height] duration-300 sm:gap-6", scrolled ? "h-[60px]" : "h-[76px]")}>
        <Logo compact={scrolled} light={dark} />
        <nav className="hidden items-center lg:flex" aria-label="Hovudmeny" onMouseLeave={() => setHover(null)}>
          {NAV.map((n) => {
            const on = active === n.id;
            return (
              <Link
                key={n.id}
                to={href(n)}
                onMouseEnter={() => setHover(n.id)}
                onFocus={() => setHover(n.id)}
                onBlur={() => setHover(null)}
                aria-current={on ? "page" : undefined}
                data-testid={`topnav-${n.id}-link`}
                className={cn("relative px-3 py-2.5 text-[14px] font-medium transition-colors duration-200 xl:px-3.5 xl:text-[14.5px] focus-visible:outline-none",
                  dark ? (on ? "text-paper" : "text-paper/60 hover:text-paper focus-visible:text-paper") : (on ? "text-foreground" : "text-foreground/60 hover:text-foreground focus-visible:text-foreground"))}
              >
                {n.label}
                {target === n.id && (
                  <motion.span
                    layoutId="topnav-indicator"
                    className="absolute bottom-0 left-3 right-3 h-[2px] rounded-full bg-sol xl:left-3.5 xl:right-3.5"
                    transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 520, damping: 42, mass: 0.6 }}
                    aria-hidden="true"
                  />
                )}
              </Link>
            );
          })}
        </nav>
        <div className={cn("flex items-center gap-1 sm:gap-2", dark && "[&_button]:text-paper [&_button:hover]:bg-paper/10")}>
          <Button asChild size="sm" variant={dark ? "lightSolOutline" : "solOutline"} className="hidden h-10 rounded-full px-4 hover:-translate-y-px md:inline-flex" data-testid="topnav-arranger-link">
            <Link to="/arranger">Arranger noko <Sparkles className="h-3.5 w-3.5" /></Link>
          </Button>
          <Button asChild size="sm" variant={dark ? "light" : "default"} className="hidden h-10 rounded-full px-4 sm:inline-flex" data-testid="topnav-booking-link">
            <Link to="/leig-rom">Leig rom <ArrowUpRight className="h-3.5 w-3.5" /></Link>
          </Button>
          <ChatHeaderButton />
          <MobileMenu site={site} />
        </div>
      </div>
    </header>
  );
};

const Footer = ({ site }) => {
  const email = site?.contact_email || "post@halsnoykulturhus.no";
  return (
    <footer className="grain mt-8 bg-ink text-paper sm:mt-12" data-testid="site-footer">
      <div className={cn(GUTTER, "pb-6 pt-16 sm:pt-20")}>
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-6">
            <Logo light testId="footer-logo-link" className="[&>svg]:w-[232px] sm:[&>svg]:w-[280px] lg:[&>svg]:w-[300px]" />
            <h2 className="t-h2 mt-8 max-w-xl text-balance text-paper">Eit hus bygd, eigd og drive av <em className="t-swash">folket</em> på øya.</h2>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild variant="light" size="lg" data-testid="footer-booking-link"><Link to="/leig-rom">Leig rom <ArrowUpRight className="h-4 w-4" /></Link></Button>
              <Button asChild variant="lightOutline" size="lg" data-testid="footer-arranger-link"><Link to="/arranger">Arranger noko</Link></Button>
              <Button asChild variant="lightOutline" size="lg" data-testid="footer-calendar-link"><Link to="/kalender">Sjå kalenderen</Link></Button>
            </div>
          </div>
          <div className="lg:col-span-3">
            <p className="t-mono text-paper/55">Kontakt</p>
            <ul className="mt-4 space-y-3 text-[15px]">
              <li><a href={`mailto:${email}`} className="break-all u-hover" data-testid="footer-email-link">{email}</a></li>
              {site?.phone && <li><a href={`tel:${site.phone}`} className="u-hover">{site.phone}</a></li>}
              <li className="text-paper/70">{site?.address || "Sæbøvik, Halsnøy"}</li>
            </ul>
            <ul className="mt-6 flex gap-5 text-[14px] font-medium">
              {site?.facebook_url && <li><a href={site.facebook_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-paper/80 u-hover hover:text-paper" data-testid="footer-facebook-link">Facebook <ArrowUpRight className="h-3 w-3" /></a></li>}
              {site?.instagram_url && <li><a href={site.instagram_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-paper/80 u-hover hover:text-paper" data-testid="footer-instagram-link">Instagram <ArrowUpRight className="h-3 w-3" /></a></li>}
            </ul>
          </div>
          <div className="lg:col-span-3">
            <p className="t-mono text-paper/55">Innhald</p>
            <ul className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2.5 text-[15px]">
              {[["/arrangement", "Program"], ["/nytt", "Nytt frå huset"], ["/rom", "Rom"], ["/prisar", "Prisar"], ["/leig-rom", "Leig rom"], ["/arranger", "Arranger noko"], ["/kalender", "Kalender"], ["/om-oss", "Om huset"], ["/historie", "Historie"], ["/omradet", "Området"], ["/kontakt", "Kontakt"]].map(([to, label]) => (
                <li key={to}><Link to={to} className="text-paper/80 u-hover hover:text-paper">{label}</Link></li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 grid gap-6 border-t border-paper/15 pt-10 lg:grid-cols-12 lg:items-center lg:gap-8" data-testid="footer-newsletter">
          <div className="lg:col-span-6">
            <p className="t-mono text-paper/55">Nyheitsbrev</p>
            <h2 className="mt-3 font-display text-[clamp(22px,2.2vw,30px)] font-semibold leading-[1.1] tracking-[-0.02em] text-balance text-paper">Få nytt frå huset rett i innboksen</h2>
            <p className="mt-2 max-w-md text-[15px] leading-relaxed text-paper/60">Nye arrangement, dugnad og små gledar – berre når det er noko å melde.</p>
          </div>
          <div className="lg:col-span-5 lg:col-start-8"><NewsletterSignup source="footer" testId="footer-newsletter" /></div>
        </div>

        <div className="mt-16 overflow-hidden border-t border-paper/15 pt-8" aria-hidden="true">
          <div className="whitespace-nowrap font-display text-[clamp(40px,9.4vw,168px)] font-semibold leading-[0.9] tracking-[-0.045em] text-paper/90">Halsnøy Kulturhus</div>
        </div>

        <div className="mt-6 flex flex-col gap-2 border-t border-paper/15 pb-16 pt-5 text-[13px] text-paper/55 sm:flex-row sm:items-center sm:justify-between sm:pb-0 sm:pr-24">
          <span className="flex flex-wrap items-center gap-x-5 gap-y-1"><span>© {new Date().getFullYear()} {site?.org_name || "Halsnøy Kulturhus SA"}</span><MadeBy dark withAbout /></span>
          <span className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <Link to="/arrangor" className="inline-flex items-center gap-1 u-hover hover:text-paper" data-testid="footer-organizer-link">Arrangørside – logg inn <ArrowRight className="h-3 w-3" /></Link>
            <Link to="/admin" className="inline-flex items-center gap-1 u-hover hover:text-paper" data-testid="footer-admin-link">Admin for styret <ArrowRight className="h-3 w-3" /></Link>
          </span>
        </div>
      </div>
    </footer>
  );
};

export const PublicLayout = () => {
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: fetchSite });
  usePageTracking();
  return (
    <div className="flex min-h-screen flex-col bg-background">
      <TopNav site={site} />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer site={site} />
      <ChatLauncher />
    </div>
  );
};
